import type { CatalogAddon } from '~/store/billing'

/**
 * Which step of the checkout each add-on belongs beside.
 *
 * Add-ons used to be one flat list in a single "Optional" card, placed AFTER
 * every decision it modifies: the Windows licence sat six steps below the OS
 * that requires it, extra addresses below the network step, backup slots
 * nowhere near the disk. Read in that order they are a bill of unfamiliar
 * items rather than answers to the question just asked.
 *
 * The mapping is by `addon_type` rather than `code` because the type is the
 * catalog's own vocabulary and codes are operator-editable.
 *
 * `EXTRA_DISK` and `MAIN_DISK_EXPANSION` are deliberately absent: the server
 * hides them from the add-on list entirely (`visible_in_checkout = false`) and
 * they surface as the storage step's own controls.
 */
export type CheckoutAddonSlot = 'os' | 'storage' | 'connectivity' | 'extras'

const ADDON_STEP_SLOT: Record<string, CheckoutAddonSlot> = {
  // Bought because of the OS, and already `os_filter`-gated to it.
  WINDOWS_LICENSE: 'os',
  CAL_LICENSE: 'os',
  // Protecting the data on the disk you are sizing.
  BACKUP_SLOT: 'storage',
  SNAPSHOT_SLOT: 'storage',
  // How the machine is reachable.
  EXTRA_IPV4: 'connectivity',
  EXTRA_IPV6: 'connectivity',
  ANTI_DDOS: 'connectivity',
  // About the account rather than the machine.
  PREMIUM_SUPPORT: 'extras',
}

/**
 * Where an add-on renders. **An unmapped type falls back to `extras`, and that
 * is the load-bearing rule here.**
 *
 * The catalog is data: an operator can seed a new `addon_type` and it must
 * appear somewhere the moment it does. A lookup that returned nothing would
 * make a new add-on silently unsellable, with no error anywhere and nobody to
 * notice until someone asked why nobody was buying it — the same reasoning the
 * alert inbox keeps a `generic` kind for.
 */
export function addonSlot(addon: CatalogAddon): CheckoutAddonSlot {
  return ADDON_STEP_SLOT[addon.addon_type] ?? 'extras'
}

/** The visible add-ons belonging to one step, in catalog order. */
export function addonsInSlot(
  addons: CatalogAddon[],
  slot: CheckoutAddonSlot,
  isVisible: (addon: CatalogAddon) => boolean,
): CatalogAddon[] {
  return addons.filter((addon) => isVisible(addon) && addonSlot(addon) === slot)
}
